import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {v4 as uuidv4} from 'uuid';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {
  AutoFillFeatureResultProps,
  trackAutofillFeatureResult,
  trackSubmitAutoFillFeature,
} from 'molbio/SequenceAnalyticsHelper';
import * as AutoFillFieldActions from 'registry/AutoFillFieldActions';
import {FIELD_TYPES, FieldType} from 'schemas/fields/SchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BLUE_LIGHT, BLUE_POWDER_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

export interface AutoFillLinkProps {
  isEditing: boolean;
  isOligo: boolean;
  fileId?: string;
  areTagsEditable: boolean;
  tagSchema: TagSchema | null;
}

interface LinkWithBannerProps {
  children: JSX.Element;
}

const styles = StyleSheet.create({
  container: {
    // add more top padding to keep autofill link in line with information panel
    padding: '10px 0px 8px',
    display: 'flex',
    flexDirection: 'column',
  },
  infoColors: {
    backgroundColor: BLUE_POWDER_LIGHT,
    borderColor: BLUE_LIGHT,
  },
});

function LinkWithBanner(props: LinkWithBannerProps): JSX.Element {
  return (
    <div className="simpleDivTable-header">
      <InlineBanner
        text="Apply autofill to visualize schema field links on sequence maps."
        bannerType={InlineBannerType.INFO}
      />
      {props.children}
    </div>
  );
}

export type AutofillNames = 'Parts' | 'Translations' | 'Transcriptions';

/**
 * Link prompting to run auto-fill on parts, translations, or transcriptions, as applicable.
 *
 * If none are applicable, this renders to nothing. If just one is applicable, this is a link performing
 * that action. If multiple are applicable, this is a link dropdown letting the user pick which action to take.
 */
export default function AutoFillLink({
  isEditing,
  isOligo,
  fileId,
  areTagsEditable,
  tagSchema,
}: AutoFillLinkProps): JSX.Element | null {
  // Only render if this is a non-oligo sequence with an editable bioentity link field (part/translation/transcription).
  // Start with some basic checks before checking bioentity links.
  if (!fileId || !areTagsEditable || !tagSchema || isOligo || isEditing) {
    return null;
  }
  interface AutofillActions {
    name: AutofillNames;
    fieldType: FieldType;
    autofillActionFn: () => void;
  }

  function fieldTypePresentInSchema(fieldType: FieldType): Boolean {
    return tagSchema!.fields.some((f) => f.fieldType === fieldType);
  }

  const autofillCompatibleBioentities: Array<AutofillActions> = [
    {
      name: 'Parts',
      fieldType: FIELD_TYPES.PART_LINK.type,
      autofillActionFn: () => {
        trackSubmitAutoFillFeature([fileId], ActionOrigin.ENTITY_PAGE_METADATA_TAB_SCHEMA_TABLE, 'Parts');
        AutoFillFieldActions.autoFillPartFields(fileId);
      },
    },
    {
      name: 'Transcriptions',
      fieldType: FIELD_TYPES.TRANSCRIPTION_LINK.type,
      autofillActionFn: () => {
        const autofillRunUID = uuidv4();
        trackSubmitAutoFillFeature(
          [fileId],
          ActionOrigin.ENTITY_PAGE_METADATA_TAB_SCHEMA_TABLE,
          'Transcriptions',
          autofillRunUID
        );
        AutoFillFieldActions.autofillTranscriptions(
          fileId,
          ({sequenceId, autofillCount, autofillErrors}: AutoFillFeatureResultProps) => {
            trackAutofillFeatureResult({
              actionOrigin: ActionOrigin.ENTITY_PAGE_METADATA_TAB_SCHEMA_TABLE,
              autofillRunUID,
              autofillName: 'Transcriptions',
              sequenceId,
              autofillCount,
              autofillErrors,
            });
          }
        );
      },
    },
    {
      name: 'Translations',
      fieldType: FIELD_TYPES.TRANSLATION_LINK.type,
      autofillActionFn: () => {
        trackSubmitAutoFillFeature(
          [fileId],
          ActionOrigin.ENTITY_PAGE_METADATA_TAB_SCHEMA_TABLE,
          'Translations'
        );
        AutoFillFieldActions.autofillTranslations(fileId);
      },
    },
  ];

  const selectables = [...autofillCompatibleBioentities].filter((autofillAction) =>
    fieldTypePresentInSchema(autofillAction.fieldType)
  );
  const selectablesCount = selectables.length;
  if (selectablesCount > 1) {
    return (
      <LinkWithBanner>
        <div className={css(styles.container, styles.infoColors)}>
          <Dropdown
            menu={
              <Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.MEDIUM}>
                {selectables.map((autofillAction) => (
                  <Option key={autofillAction.name} onSelect={autofillAction.autofillActionFn}>
                    {autofillAction.name}
                  </Option>
                ))}
              </Menu>
            }
          >
            <a className="u-mini-spacing-left">
              Autofill <BenchlingIcon icon="chevron-down" />
            </a>
          </Dropdown>
        </div>
      </LinkWithBanner>
    );
  } else if (selectablesCount === 1) {
    return (
      <LinkWithBanner>
        <div className={css(styles.container, styles.infoColors)}>
          <a
            className="u-mini-spacing-left"
            onClick={selectables[0].autofillActionFn}
            {...getTestProps({component: 'AutoFillLink', element: `autofill${selectables[0].name}`})}
          >
            Autofill {selectables[0].name.toLowerCase()}
          </a>
        </div>
      </LinkWithBanner>
    );
  } else {
    return null;
  }
}
